/**
 * Liste des groupes sauvegardés dans le dashboard
 */

import React, { useState } from "react";
import { useGroups } from "@core/hooks/useGroups";
import { Users, Edit2, Trash2, Mail } from "lucide-react";
import { useToast } from "@core/hooks/use-toast";
import { GroupEditor } from "./GroupEditor";
import { useTranslation } from "react-i18next";

export function GroupList() {
  const { groups, deleteGroup, loading } = useGroups();
  const { toast } = useToast();
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);
  const { t } = useTranslation("polls");

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(t("groups.delete.confirm", { name }))) {
      return;
    }

    setDeletingGroupId(id);
    const success = await deleteGroup(id);
    setDeletingGroupId(null);

    if (success) {
      toast({
        title: t("groups.delete.successTitle"),
        description: t("groups.delete.successDescription", { name }),
      });
    } else {
      toast({
        title: t("groups.delete.errorTitle"),
        description: t("groups.delete.errorDescription"),
        variant: "destructive",
      });
    }
  };

  if (loading && groups.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        {t("groups.loading")}
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <Users className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          {t("groups.empty.title")}
        </h3>
        <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          {t("groups.empty.description")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
        <Users className="w-5 h-5" />
        {t("groups.title", { count: groups.length })}
      </h2>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {groups.map((group) => (
          <div
            key={group.id}
            className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                  {group.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                  <Mail className="w-3 h-3" />
                  {t("groups.memberCount", {
                    count: group.member_emails.length,
                  })}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="max-h-20 overflow-y-auto text-xs text-gray-600 dark:text-gray-400 space-y-1">
                {group.member_emails.slice(0, 3).map((email, idx) => (
                  <div key={idx} className="truncate">
                    • {email}
                  </div>
                ))}
                {group.member_emails.length > 3 && (
                  <div className="text-gray-400 dark:text-gray-500">
                    {t("groups.moreMembers", {
                      count: group.member_emails.length - 3,
                    })}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={() => setEditingGroupId(group.id)}
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center gap-1"
                >
                  <Edit2 className="w-3 h-3" />
                  {t("groups.edit")}
                </button>
                <button
                  onClick={() => handleDelete(group.id, group.name)}
                  disabled={deletingGroupId === group.id}
                  className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal d'édition */}
      {editingGroupId && (
        <GroupEditor
          groupId={editingGroupId}
          onClose={() => setEditingGroupId(null)}
        />
      )}
    </div>
  );
}
